// The persisted chat-message shape, as pure functions.
//
// Extracted from ChatPanel.js for the same reason `chatRounds.js` was: ChatPanel
// cannot be rendered under jest (it pulls the whole page's dependency graph), so
// anything inside it is untestable. These three decide what survives a
// save/reload round trip, which is exactly the kind of thing that fails silently.

// A message may carry navigable targets alongside its text — today
// `[{kind:'tile', tileId, label}]`, written by a server-side agent run when a
// module is waiting on the user (HS-190).
//
// It is a SIBLING of `content`, exactly as `toolSummary` is, rather than a link
// inside the text. Two reasons, both load-bearing: `content` is rendered as raw
// text (there is no markdown renderer and no `dangerouslySetInnerHTML` anywhere in
// `src/`), and a `tileId` is an opaque `crypto.randomUUID()` that must be resolved
// server-side — never composed into a URL by a model.
//
// Unknown kinds and entries with no `tileId` are DROPPED rather than passed
// through: the renderer would have nothing to do with them, and a malformed entry
// reaching the UI as a dead control is worse than no control.
export function normalizeActions(actions) {
  if (!Array.isArray(actions)) return null;
  const kept = actions
    .filter((a) => a && a.kind === 'tile' && a.tileId)
    .map((a) => ({ kind: 'tile', tileId: a.tileId, label: a.label }));
  return kept.length ? kept : null;
}

// What the user should SEE, when that differs from what the model was sent.
//
// `content` is the LLM's own text and it is not always the user's: the composer
// wraps a typed prompt in kickoff scaffolding (`pages/agentKickoff.js`), and the
// orchestration loop synthesizes whole user turns — "(You are now the subagent for
// the mesh module …)", "(The user replied to your question "…": yes)". Those are
// inputs to a model, not things a person said, but they must still be PERSISTED,
// because the stored history is also the history replayed into the next prompt
// (`agent_runner/chat_io.load_history`). So the split is a render-time one:
//
//   absent  → render `content` (every message stored before this field existed)
//   string  → render this instead
//   ''      → render nothing; a pure machine turn
//
// `''` and absent must therefore stay DISTINGUISHABLE — `null` for "no override",
// never coalesced with the empty string, or every machine turn becomes visible
// again. `normalizeDisplay` is what keeps that honest.
export function normalizeDisplay(display) {
  return typeof display === 'string' ? display : null;
}

// What gets written to the backend. The `typeof content === 'string'` filter is
// why in-round tool_use/tool_result blocks never persist — a known Roadmap item,
// and the server-side runner matches it deliberately so the two execution paths
// leave interchangeable histories.
//
// `actions` MUST be carried here as well as in `fromSaved`: dropping it on save
// would strip the link from the stored history, which is the one place the
// question has to survive a reload.
export function toSaveable(msgs) {
  return msgs
    .filter((m) => !m.streaming && typeof m.content === 'string')
    .map((m) => {
      const actions = normalizeActions(m.actions);
      const display = normalizeDisplay(m.display);
      return {
        role: m.role,
        content: m.content || '',
        toolSummary: m.toolSummary || null,
        error: m.error || false,
        // Omitted when absent, so a message with no actions keeps the exact shape
        // it had before this field existed.
        ...(actions ? { actions } : {}),
        // Same rule, and `!== null` rather than a truthiness test: `''` is a
        // MEANINGFUL value here ("show nothing"), so a falsy check would drop it
        // and make every synthesized turn visible on the next reload.
        ...(display !== null ? { display } : {}),
      };
    });
}

export function fromSaved(msgs) {
  return msgs.map((m) => ({
    role: m.role,
    content: m.content || '',
    toolSummary: m.toolSummary || null,
    error: m.error || false,
    streaming: false,
    actions: normalizeActions(m.actions),
    display: normalizeDisplay(m.display),
  }));
}

// One message in the shape both this panel and the backend's
// `tutorial_runner/narration.py` `_message` write. Kept here so the two sides have
// one documented shape to agree on.
export function localMessage(content, role = 'assistant', actions = null, display = null) {
  return {
    role,
    content,
    toolSummary: null,
    error: false,
    streaming: false,
    actions: normalizeActions(actions),
    display: normalizeDisplay(display),
  };
}
